import { useMutation, useQueryClient } from '@tanstack/react-query'

import { useAuthInfo } from '@sweetauth/core'

import { API_URL } from '@/constants/env'

import {
  convertDatesInObject,
  insertSingleAtBeginningOfPage,
} from '@/helpers/api'
import { SweetspotError } from '@/helpers/error'

import { useHandleError } from '@/hooks/common/useErrorHandler'
import { pursuitsQueryKeys } from '@/hooks/pursuits/types'

import type { PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { CreatePursuit, Pursuit } from '@/types/pursuit/Pursuit'

interface UseCreatePursuitArgs {
  orgId: UUID | ''
}

const createPursuit = async (
  pursuit: CreatePursuit,
  accessToken: string | undefined | null,
) => {
  if (!accessToken) {
    throw new SweetspotError({
      name: 'NO_ACCESS_TOKEN_PROVIDED_ERROR',
      message: 'No access token provided',
    })
  }
  const res = await fetch(`${API_URL}/api/v1/pursuits/`, {
    method: 'POST',
    body: JSON.stringify(pursuit),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok) {
    throw new SweetspotError({
      name: 'CREATE_PURSUIT_ERROR',
      message: res.statusText,
      cause: {
        status: res.status,
      },
    })
  }
  return convertDatesInObject(await res.json())
}

export const useCreatePursuit = ({ orgId }: UseCreatePursuitArgs) => {
  const { accessToken } = useAuthInfo()
  const { handleError } = useHandleError()

  const queryClient = useQueryClient()

  return useMutation<Pursuit, Error, CreatePursuit, void>({
    mutationFn: (pursuit) => createPursuit(pursuit, accessToken),
    onError: (err, pursuit, context) => {
      // rollback to the previous value
      // TODO: figure out how to properly handle this error, since it propagates to the user
      const key = pursuitsQueryKeys.organization({ orgId })
      queryClient.setQueryData(key, context)
      handleError(err)
    },
    onSuccess: (data, pursuit) => {
      const key = pursuitsQueryKeys.organization({ orgId })
      queryClient.setQueryData<PaginatedResponse<Pursuit>>(key, (oldData) =>
        insertSingleAtBeginningOfPage(data, oldData),
      )
      queryClient.invalidateQueries({ queryKey: key })
      queryClient.invalidateQueries({
        queryKey: pursuitsQueryKeys.organizationsOverviewAll(),
      })
      // Also invalidate latest opportunity pursuit queries so the UI updates
      queryClient.invalidateQueries({ queryKey: ['pursuits', 'latest'] })
      return pursuit
    },
  })
}
